import { TableInvoiceRow, TablePaymentRow, PaymentDetail } from "../types/invoice-table.types";

export const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

export interface PaymentSummaryBreakdown {
	month: string; // Format: "2024-01" for sorting
	displayMonth: string; // Format: "Jan 2024" for display
	openAmount: number;
	paidAmount: number;
	invoiceCount: number;
	paymentCount: number;
	currency: string;
}

export interface OpenAmountSummary {
	totalOpenAmount: number;
	totalInvoiceCount: number;
	monthlyBreakdown: PaymentSummaryBreakdown[];
	currency: string;
}

export interface PaidAmountSummary {
	totalPaidAmount: number;
	totalPaymentCount: number;
	monthlyBreakdown: PaymentSummaryBreakdown[];
	currency: string;
}

/**
 * Formats a date string into month identifier for grouping
 */
function formatMonthForGrouping(dateString: string): string {
	const date = new Date(dateString);
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Formats a month identifier into display format
 */
function formatMonthForDisplay(monthKey: string): string {
	const [year, month] = monthKey.split('-');
	const date = new Date(parseInt(year), parseInt(month) - 1);
	return date.toLocaleDateString('de-DE', {
		month: 'short',
		year: 'numeric'
	});
}

/**
 * Calculates open amounts grouped by month for Offene Posten page
 */
export function calculateOpenAmountSummary(invoices: TableInvoiceRow[]): OpenAmountSummary {
	let totalOpenAmount = 0;
	let totalInvoiceCount = 0;
	let currency = 'CHF';

	invoices.forEach(invoice => {
		const invoiceValue = invoice.invoice_value || 0;

		// Calculate allocated amount from payments (excluding losses)
		const totalPaid = (invoice.payments as unknown as TablePaymentRow[])?.reduce((sum, payment) => {
			// Exclude losses (payment_id === null) and canceled allocations
			if (payment.allocation_canceled_at || payment.payment_id === null) {
				return sum;
			}
			return sum + (payment.allocated_amount || 0);
		}, 0) || 0;

		const openAmount = Math.max(0, Math.abs(invoiceValue) - totalPaid);
		currency = invoice.invoice_currency || currency;

		totalOpenAmount += openAmount;
		totalInvoiceCount += 1;
	});

	return {
		totalOpenAmount,
		totalInvoiceCount,
		monthlyBreakdown: [], // Empty since we don't need monthly breakdown
		currency,
	};
}

/**
 * Calculates paid amounts grouped by month for Zahlungsjournal page
 */
export function calculatePaidAmountSummary(invoices: TableInvoiceRow[]): PaidAmountSummary {
	let totalPaidAmount = 0;
	let totalPaymentCount = 0;
	let currency = 'CHF';

	invoices.forEach(invoice => {
		const payments = invoice.payments as unknown as TablePaymentRow[];
		if (!payments || !Array.isArray(payments)) return;

		payments.forEach(payment => {
			// Exclude losses (payment_id === null) from paid amount
			if (!payment.payment_created_at || !payment.allocated_amount || payment.payment_id === null) return;

			const paidAmount = payment.allocated_amount;
			currency = payment.payment_currency || invoice.invoice_currency || currency;

			totalPaidAmount += paidAmount;
			totalPaymentCount += 1;
		});
	});

	return {
		totalPaidAmount,
		totalPaymentCount,
		monthlyBreakdown: [], // Empty since we don't need monthly breakdown
		currency,
	};
}

/**
 * Calculates the total allocated payment amount for an invoice for cancellation.
 * 
 * @param invoice - The invoice object to calculate payments for, or null/undefined
 * @returns Total allocated amount (0 if invoice not found or no valid payments)
 */
export function calculatePaymentAmountForCancellation(
	invoice: TableInvoiceRow | null | undefined
): number {
	if (!invoice) return 0;
	
	const payments = invoice.payments as unknown as TablePaymentRow[] | PaymentDetail[] | null;
	if (!payments || !Array.isArray(payments)) return 0;
	
	return payments.reduce((sum: number, payment) => {
		const isCanceled = payment.payment_id === null || 
			(payment.allocation_canceled_at !== null && 
			payment.payment_canceled_at !== null);
		
		return isCanceled ? sum : sum + (payment.allocated_amount || 0);
	}, 0);
}